import { createApp } from 'vue';

// Use the same server-rendered composer and mixins as the client account, with isolated draft state.
export default function registerMessageReplyComposer(app) {
    app.component('message-reply-composer', {
        props: { url: { type: String, required: true } },
        emits: ['sending', 'sent'],
        data: () => ({ loading: false, error: '', sending: false, sequence: 0, initialSubject: '', initialContent: '' }),
        template: `<div class="admin2026-message-composer" data-admin2026-message-reply-composer="true">
            <p v-if="loading" role="status" class="mb-4 text-sm text-slate-500">Loading reply editor…</p>
            <div v-if="error" role="alert" class="mb-4 text-sm text-rose-600">
                {{ error }} <button type="button" class="tbtn tbtn-outline" @click="load">Try again</button>
            </div>
            <div ref="content"></div>
        </div>`,
        mounted() {
            this.modalCallback = () => {
                const action = this.pendingAction;
                this.pendingAction = null;
                if (action?.state === this.formState) action.state[action.method]?.(...action.args);
            };
            this.$root.messageReaderComposerAction = this.modalCallback;
            this.load();
        },
        watch: { url() { this.load(); } },
        beforeUnmount() {
            ++this.sequence;
            this.dispose();
            if (this.$root.messageReaderComposerAction === this.modalCallback) delete this.$root.messageReaderComposerAction;
        },
        methods: {
            hasChanges() {
                const state = this.formState;
                if (!state) return this.sending;
                const content = value => String(value || '').replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '').trim();
                return Boolean(this.sending || state?.formTouched || state?.messageDraftSaving
                    || String(state.subject || '') !== this.initialSubject || content(state.quillContent) !== content(this.initialContent)
                    || state?.uploadAssets?.[1]?.length || state?.messageSnippetAssets?.length
                    || state?.videoRecorderStatus || state?.videoRecorderUploadProcessing || state?.videoRecorderUploadUrl);
            },
            dispose() {
                this.pendingAction = null;
                this.contentApp?.unmount();
                this.contentApp = null;
                this.formState = null;
            },
            forwardModal(state, method, args, callbackIndex) {
                if (typeof args[callbackIndex] === 'string' && args[callbackIndex]) {
                    this.pendingAction = { state, method: args[callbackIndex], args: args.slice(callbackIndex + 1, callbackIndex + 4) };
                    args[callbackIndex] = 'messageReaderComposerAction';
                    args.splice(callbackIndex + 1, 3, null, null, null);
                }
                this.$root[method](...args);
            },
            async load() {
                const sequence = ++this.sequence;
                this.loading = true;
                this.error = '';
                this.dispose();
                try {
                    const target = new URL(this.url, window.location.href);
                    if (target.origin !== window.location.origin || !/^\/pono-admin\/workspaces\/clients\/messages\/\d+\/reply-composer$/.test(target.pathname)) throw new Error('invalid_composer_url');
                    const response = await this.$root.axios.post(target.pathname, {}, { headers: { Accept: 'application/json' } });
                    if (sequence !== this.sequence) return;
                    if (response.data?.success !== true || !response.data.content || !response.data.mixin) throw new Error('composer_unavailable');
                    const owner = this;
                    // Only the authenticated, same-origin reply-composer endpoint supplies this trusted Blade mixin.
                    const mixin = new Function('return (' + response.data.mixin + ')')();
                    const assetPreviews = {};
                    for (const name of ['openModalAsset1', 'openModalAsset11', 'openModalAsset13', 'openModalAsset14', 'openModalAsset15', 'openModalAsset16', 'openModalAsset17']) {
                        assetPreviews[name] = (...args) => owner.$root[name]?.(...args);
                    }
                    const contentApp = createApp({
                        template: response.data.content,
                        mixins: [{ data: app._component.data, methods: app._component.methods, computed: app._component.computed }, mixin],
                        methods: {
                            ...assetPreviews,
                            submitForm() { return owner.send(this); },
                            touchForm() { this.formTouched = true; this.messageDraftSaved = false; },
                            updateQuill(id, target) {
                                if (this.$refs.form) app._component.methods.updateQuill.call(this, id, target, this.$refs.form);
                            },
                            messageDraftDidSave(snapshot) {
                                if (this !== owner.formState) return;
                                owner.initialSubject = snapshot.subject;
                                owner.initialContent = snapshot.content;
                                this.formTouched = String(this.subject || '') !== snapshot.subject
                                    || String(this.quillContent || '') !== snapshot.content;
                            },
                            admin2026ClearDirtyFormState() {
                                this.formTouched = false;
                                owner.initialSubject = String(this.subject || '');
                                owner.initialContent = String(this.quillContent || '');
                            },
                            openModalWarning: (...args) => owner.$root.openModalWarning(...args),
                            openModalPlaceholders: () => owner.$root.openModalPlaceholders(true),
                            openModalOk(...args) { owner.forwardModal(this, 'openModalOk', args, 3); },
                            openModalConfirm(...args) { owner.forwardModal(this, 'openModalConfirm', args, 4); },
                            showOverlay: (...args) => owner.$root.showOverlay(...args),
                            hideOverlay: (...args) => owner.$root.hideOverlay(...args),
                        },
                    });
                    Object.entries(app._context.components).forEach(([name, component]) => contentApp.component(name, component));
                    for (const [, name] of response.data.content.matchAll(/<([A-Z][A-Za-z0-9]*)\b/g)) {
                        const component = app._context.components[name.toLowerCase()];
                        if (component) contentApp.component(name, component);
                    }
                    Object.entries(app._context.directives).forEach(([name, directive]) => contentApp.directive(name, directive));
                    contentApp.config.globalProperties.axios = this.$root.axios;
                    this.contentApp = contentApp;
                    this.formState = contentApp.mount(this.$refs.content);
                    this.initialSubject = String(this.formState.subject || '');
                    this.initialContent = String(this.formState.quillContent || '');
                } catch (_) {
                    if (sequence === this.sequence) this.error = 'The reply editor could not be loaded. Please try again.';
                } finally {
                    if (sequence === this.sequence) this.loading = false;
                }
            },
            async send(state) {
                if (this.sending || state !== this.formState) return;
                const form = state.$refs.form;
                if (!form) { state.messageSendInFlight = false; return; }
                this.sending = true;
                state.messageSendInFlight = true;
                form.inert = true;
                this.$emit('sending', true);
                try {
                    if (state.messageDraftSavePromise) await state.messageDraftSavePromise.catch(() => {});
                    await state.$nextTick();
                    state.submitFormAdditional();
                    const payload = new FormData(form);
                    const send = async (attempt = 0) => {
                        try {
                            return await this.$root.axios.post(form.action, payload, { headers: { Accept: 'application/json' } });
                        } catch (error) {
                            const status = Number(error?.response?.status || 0);
                            const pending = status === 409 && error?.response?.data?.pending === true;
                            const uncertain = status === 0 || status === 408 || status === 425 || status >= 500;
                            if (attempt < 3 && (pending || uncertain)) {
                                await new Promise(resolve => window.setTimeout(resolve, 500));
                                return send(attempt + 1);
                            }
                            throw error;
                        }
                    };
                    const response = await send();
                    if (response.data?.success !== true) throw new Error('send_not_confirmed');
                    this.$root.showToastAdmin(response.data.message || 'Message sent.');
                    this.$emit('sent', response.data);
                    await this.load();
                } catch (error) {
                    this.$root.openModalWarning('Message not confirmed', error?.response?.data?.message
                        || 'We could not confirm whether the message was sent. Your text is still here. Check the conversation before trying again.');
                } finally {
                    form.inert = false;
                    state.messageSendInFlight = false;
                    this.sending = false;
                    this.$emit('sending', false);
                }
            },
        },
    });
}
